import { useTenant } from '@emerald-works-nova/auth'
import React, { createContext, useState } from 'react'
import { useGroup, useIsStudentPlan, useProducts } from '../../hooks'
import { ALL_ADMIN, ALL_ENTERPRISE, BROWSER, CONSUMER } from '../../services/cognito/userGroups'
import consts from '../../consts'
import { useSession } from '@emerald-works/react-auth'
import { useSelector } from 'react-redux'
import { contentSlice, onboardingSlice } from '../../reducers'
import MindsetAgent from '.'
import { useEvent } from '@emerald-works/react-event-bus-client'
import { useGleap } from '../gleap'

const MindsetContext = createContext()

const MindsetProvider = ({ children }) => {
  const tenant = useTenant()
  const mindsetAgentRef = React.useRef(null)

  const isPreview = useSelector(contentSlice.selectors.selectIsPreview)
  const { isInGroup } = useGroup()
  const { isAuthenticated } = useSession()
  const { hasContentHubAccess } = useProducts()
  const isClubBrowserUser = tenant.pk === consts.DEFAULT_CLUB_PK && (isInGroup(BROWSER) || !isAuthenticated)
  const { isStudentPlan } = useIsStudentPlan()
  const shouldShowMindsetAi = isAuthenticated && (isInGroup([...ALL_ENTERPRISE, ...ALL_ADMIN, CONSUMER]) || isClubBrowserUser) && hasContentHubAccess && tenant.features?.mindsetAi && isStudentPlan === false
  const [isOpen, setOpen] = useState(false)
  const [hasBeenOpened, setHasBeenOpened] = useState(false)

  const setOpenWithTracking = (open) => {
    if (!hasBeenOpened && open) {
      setHasBeenOpened(true)
    }
    setOpen(open)
  }

  return (
    <MindsetContext.Provider value={{ mindsetAgentRef, shouldShowMindsetAi, hasBeenOpened, shouldTrackOpenEvent: !isClubBrowserUser, isOpen, setOpen: setOpenWithTracking }}>
      <>
        {shouldShowMindsetAi && !isPreview && (
          <MindsetAgent
            open={isOpen}
            setOpen={setOpenWithTracking}
            ref={mindsetAgentRef}
          />
        )}
        {children}
      </>
    </MindsetContext.Provider>
  )
}

const useMindset = () => {
  const EXCLUDED_ROUTES = ['/signup', '/login', '/profile']
  // eslint-disable-next-line no-undef
  const isMindesetExcludedRoute = EXCLUDED_ROUTES.some(route => location.pathname.startsWith(route))
  const { mindsetAgentRef, shouldShowMindsetAi, shouldTrackOpenEvent, isOpen, hasBeenOpened } = React.useContext(MindsetContext)
  const [askMCoachView] = useEvent([{ eventName: 'askMCoachView' }])
  const { trackEvent } = useGleap()
  const { mcoach: checklistMcoachCompleted } = useSelector(onboardingSlice.selectors.selectChecklistItems)

  const [onboardingItemTrackEvent] = useEvent([
    onboardingSlice.eventBus.onboardingChecklistItemTrackEvent
  ])

  const openMindset = React.useCallback(({ initialQuestion, from }) => {
    if (!mindsetAgentRef.current || isOpen) {
      return
    }

    if (shouldTrackOpenEvent) {
      askMCoachView.trigger({
        from,
        initialQuestion
      })
      trackEvent('Mindset Agent Opened')
      if (!checklistMcoachCompleted) {
        onboardingItemTrackEvent.trigger({
          type: 'checklistMcoach'
        })
      }
    }

    mindsetAgentRef.current.open({ initialQuestion, from })
  }, [mindsetAgentRef, askMCoachView, isOpen, shouldTrackOpenEvent, trackEvent, checklistMcoachCompleted, onboardingItemTrackEvent])

  const openMindsetNewSession = React.useCallback(({ initialQuestion, from }) => {
    if (!mindsetAgentRef.current) {
      return
    }

    if (shouldTrackOpenEvent) {
      askMCoachView.trigger({
        from,
        initialQuestion
      })
      trackEvent('Mindset Agent Opened')
    }

    mindsetAgentRef.current.openNewSession({ initialQuestion, from })
  }, [mindsetAgentRef, askMCoachView, shouldTrackOpenEvent, trackEvent])

  const closeMindset = React.useCallback(() => {
    if (mindsetAgentRef.current) {
      mindsetAgentRef.current.close()
    }
  }, [mindsetAgentRef])

  return {
    openMindset,
    openMindsetNewSession,
    closeMindset,
    shouldShowMindsetAi,
    mindsetAgentRef,
    hasBeenOpened,
    isOpen,
    isMindesetExcludedRoute
  }
}

export { MindsetProvider, useMindset }
